import useCardLayoutWithImpressions from '@/hooks/useCardLayoutWithImpressions'
import { trpc } from '@/utils/trpc'
import { useEffect } from 'react'
import { useInView } from 'react-intersection-observer'
import type { AgenticInspirationType, AgenticTemplateType } from '../../../../src/shared/types'

export function useTypingSearchAI(searchTerm: string, type: 'templates' | 'inspirations') {
  const LIMIT = 20

  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } = trpc.agenticSearch.useInfiniteQuery(
    { query: searchTerm, type, limit: LIMIT },
    { getNextPageParam: (lastPage) => lastPage.cursor },
  )

  const { ref: loadMoreRef, inView } = useInView({
    threshold: 0,
    trackVisibility: true,
    delay: 100,
  })

  useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage && data && data.pages[data.pages.length - 1]?.data.length > 0) {
      fetchNextPage()
    }
  }, [inView, hasNextPage, isFetchingNextPage, fetchNextPage])

  const allItems =
    data?.pages.flatMap((page) =>
      page.data.map((item) => ({
        ...item,
        categories: item.categories ?? [],
        semanticSummary: item.semanticSummary ?? '',
        createdAt: item.createdAt ?? '',
        createdTimestamp: item.createdTimestamp ?? 0,
      })),
    ) ?? []

  const adTemplatesOnly = allItems.filter((i) => i.assetType === 'ad') as AgenticTemplateType[]

  const emailTemplatesOnly = allItems.filter((i) => i.assetType === 'email') as AgenticTemplateType[]

  const landerTemplatesOnly = allItems.filter((i) => i.assetType === 'landing page') as AgenticTemplateType[]

  const adInspirationsOnly = allItems.filter((i) => i.assetType === 'ad') as AgenticInspirationType[]

  const emailInspirationsOnly = allItems.filter((i) => i.assetType === 'email') as AgenticInspirationType[]

  const landerInspirationsOnly = allItems.filter((i) => i.assetType === 'landing page') as AgenticInspirationType[]

  const { squareRef, desiredCardWidth, gutterWidth } = useCardLayoutWithImpressions()

  return {
    isLoading,
    isFetchingNextPage,
    adTemplatesOnly,
    emailTemplatesOnly,
    landerTemplatesOnly,
    adInspirationsOnly,
    emailInspirationsOnly,
    landerInspirationsOnly,
    loadMoreRef,
    squareRef,
    desiredCardWidth,
    gutterWidth,
  }
}
